import React from 'react';
import PropTypes from 'prop-types';

import { useField } from 'formik';
import Label from '~components/forms/Label';
import FieldHelpText from '~components/forms/HelpText';

export function SelectStandalone({
  elementId,
  name,
  value,
  onChange,
  disabled,
  children,
}) {
  return (
    <select
      name={name}
      id={elementId}
      aria-label={name}
      onChange={onChange}
      value={value}
      disabled={disabled}
      className='select select-bordered w-full border-base-content bg-base-200 bg-[url("/img/arrow-drop-down.svg")] bg-[length:1.5rem] bg-[center_right_0.5rem] text-base focus:border-base-content'
    >
      {children}
    </select>
  );
}

SelectStandalone.propTypes = {
  elementId: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

SelectStandalone.defaultProps = {
  disabled: false,
};

function SelectGroup({
  autoComplete,
  extraLabelledBy,
  label,
  name,
  options,
  errors,
  addBlank,
  blankLabel,
  helpText,
  elementId,
  required,
}) {
  const [field, meta] = useField({ name });
  const error = !!(meta.touched && meta.error) || !!errors?.[name];
  const ariaLabels = [`label__${name}`];

  if (extraLabelledBy) {
    ariaLabels.push(extraLabelledBy);
  }

  if (helpText) {
    ariaLabels.push(`helptext__${name}`);
  }

  const ariaLabelledBy = ariaLabels.join(' ');

  return (
    <div className='form-control w-full'>
      <Label htmlFor={name} id={`label__${name}`} isRequired={required}>
        {label}
      </Label>
      <select
        className={`select select-bordered w-full bg-base-200 bg-[url('/img/arrow-drop-down.svg')] bg-[length:1.5rem] bg-[center_right_0.5rem] 
        text-base focus:border-2 focus:outline-none ${
          error
            ? 'border-primary focus:border-primary'
            : 'border-base-content focus:border-base-content'
        }`}
        name={name}
        id={name}
        autoComplete={autoComplete}
        aria-labelledby={ariaLabelledBy}
        data-element-id={elementId}
        required={required}
        {...field}
      >
        {addBlank && (
          <option disabled defaultValue value=''>
            {blankLabel || 'Select'}
          </option>
        )}
        {options.map(({ option, value }) => (
          <option key={`${value}${label}`} value={value}>
            {option}
          </option>
        ))}
      </select>

      {error && (
        <FieldHelpText isError>{meta.error || errors?.[name]}</FieldHelpText>
      )}
      {helpText && (
        <FieldHelpText id={`helptext__${name}`}>{helpText}</FieldHelpText>
      )}
    </div>
  );
}

SelectGroup.propTypes = {
  autoComplete: PropTypes.string,
  elementId: PropTypes.string,
  extraLabelledBy: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  addBlank: PropTypes.bool,
  blankLabel: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  errors: PropTypes.object.isRequired,
  helpText: PropTypes.string,
  required: PropTypes.bool,
};

SelectGroup.defaultProps = {
  addBlank: false,
};

export default SelectGroup;
